import { isInteger } from 'lodash';
import { Form, Formik, FormikValues } from 'formik';
import React, { useEffect, useRef } from 'react';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import FormikButton from '../../../common/formik/FormikButton';
import FormikGrid from '../../../common/formik/FormikGrid';
import FormikTextField from '../../../common/formik/FormikTextField';
import FormikSelectField from '../../../common/formik/FormikSelectField';
import { useFindGroups } from '../../groups/Group.hooks';
import { Group } from '../../groups/Group.interface';
import { useAddTrainingGroup } from '../Training.hooks';
import { Training, TrainingGroupDto } from '../Training.interface';

export interface TrainingAddGroupDialogProps {
  open: boolean;
  training: Training;
  existingGroups: TrainingGroupDto[];
  onClose?: () => void;
  onAdded?: (group: Group) => void;
}

const TrainingAddGroupDialog: React.FC<TrainingAddGroupDialogProps> = ({
  open,
  training,
  existingGroups,
  onClose,
  onAdded
}) => {
  const formikRef = useRef<FormikValues>();
  const onAddedRef = useRef(onAdded);

  const {
    group: addedGroup,
    isLoading,
    error,
    addTrainingGroup
  } = useAddTrainingGroup(training.id);
  const { groupList } = useFindGroups(true);

  useEffect(() => {
    if (!isLoading) {
      if (formikRef && formikRef.current) {
        formikRef.current.setSubmitting(false);
      }
    }
  }, [isLoading, training]);

  useEffect(() => {
    if (addedGroup && onAddedRef && onAddedRef.current) {
      onAddedRef.current(addedGroup);
    }
  }, [error, addedGroup]);

  // remove groups that are already added to the training
  const groups = groupList
    ? groupList.data.filter(
        group => !existingGroups.find(g => g.groupId === group.id)
      )
    : [];

  return (
    <Dialog open={open} onClose={onClose}>
      <Formik
        innerRef={(formik: any) => (formikRef.current = formik)}
        initialValues={{ id: '', duration: '' as '' | number }}
        validationSchema={Yup.object({
          id: Yup.number()
            .required('Group is required.')
            .typeError('Group is required.'),
          duration: Yup.mixed().test(
            'duration',
            'Duration can only be a week or more. Clear the field to have no Due Date for this Group.',
            (value?: '' | number) => {
              return (
                value === '' ||
                typeof value === 'undefined' ||
                (value > 0 && isInteger(value))
              );
            }
          )
        })}
        onSubmit={form => {
          addTrainingGroup({ id: form.id, duration: form.duration || null });
        }}
      >
        <Form autoComplete='off'>
          <DialogTitle>Assign training to a team</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Select a team in the list below.
            </DialogContentText>
            <FormikGrid>
              {groups && (
                <FormikSelectField
                  name='id'
                  label='Select a team'
                  options={groups.map(g => {
                    return { value: g.id, label: g.name };
                  })}
                />
              )}
            </FormikGrid>

            <Box mb={2}>
              <Divider />
            </Box>

            <DialogContentText>
              Set the training <strong>Duration</strong> for the assigned team.
            </DialogContentText>
            <FormikGrid>
              <FormikTextField
                name='duration'
                label='Duration (Weeks)'
                type='number'
              />
            </FormikGrid>

            {error && (
              <Alert severity='error'>
                Error occurred.{' '}
                {error.message ||
                  'Please check your internet connection and try again.'}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <FormikButton>Add</FormikButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default TrainingAddGroupDialog;
